import { useId, useLayoutEffect, useState, type ReactNode } from 'react'
import { useIntl } from 'react-intl'

import { FieldLabelContext, InfoTip } from '@/shared/ui'
import { Info } from '@/shared/ui/icons'

/**
 * A control a person fills in or picks with, a date picker's field among them; a hidden one, such
 * as MUI's native select input, is not one.
 */
const controls =
  'input:not([type=hidden]):not([aria-hidden="true"]), select:not([aria-hidden="true"]), textarea, [role=combobox], [role=checkbox], [role=radio], [role=switch], button[aria-haspopup="dialog"]'

/**
 * Whether the row's label must name its controls as a group: it holds several, or one that its own
 * label names rather than the row's, as the check boxes under "Visible To", and no group of its
 * own. Measured again whenever the row's content changes.
 */
function useNeedsGroup(box: HTMLElement | null, labelId: string): boolean {
  const [needsGroup, setNeedsGroup] = useState(false)

  useLayoutEffect(() => {
    if (box === null) {
      return undefined
    }
    const measure = () => {
      // Not what an open popover holds, such as a date picker's days.
      const found = Array.from(box.querySelectorAll(controls)).filter((control) => {
        const popover = control.closest('dialog, [role=dialog]')
        return popover === null || !box.contains(popover)
      })
      const namedApart = found.some((control) => !(control.getAttribute('aria-labelledby')?.split(/\s+/u) ?? []).includes(labelId))
      // A row whose field is a group of its own, such as a set of check boxes, needs no second one.
      const hasOwnGroup = box.querySelector('fieldset:not([aria-hidden="true"]), [role=group], [role=radiogroup]') !== null
      setNeedsGroup(!hasOwnGroup && (found.length > 1 || namedApart))
    }
    const changes = new MutationObserver(measure)
    changes.observe(box, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-labelledby', 'role', 'type'] })
    measure()
    return () => changes.disconnect()
  }, [box, labelId])

  return needsGroup
}

/**
 * One labeled row of a create or edit form, from the mockups' `forms/FormRow.jsx`: the label in
 * a fixed column, right-aligned against the field on wider screens, stacked above it on narrow
 * ones. The fields inside take their name from the label (`FieldLabelContext`), and a required
 * row shows `*` after the field and says "required" to assistive technology. A row of several
 * controls, or of controls with labels of their own, is a group the label names (WCAG 1.3.1).
 */
export function FormRow(props: {
  readonly label: string
  /** Shows the small info icon some legacy labels carry. */
  readonly info?: boolean
  /** An info button after the label, showing `text` as a tooltip; `label` is the button's name. */
  readonly tooltip?: { readonly label: string; readonly text: string }
  readonly required?: boolean
  readonly children: ReactNode
}) {
  const intl = useIntl()
  const labelId = useId()
  const required = props.required === true
  const [fields, setFields] = useState<HTMLDivElement | null>(null)
  const isGroup = useNeedsGroup(fields, labelId)

  return (
    <div className="grid grid-cols-1 gap-1.5 py-1.5 sm:grid-cols-[220px_1fr] sm:items-center sm:gap-4">
      <span className="flex items-center gap-1.5 text-sm text-text-secondary sm:justify-end sm:text-right">
        {/* Only the label names the fields: an info button beside it stays out of their names. */}
        <span id={labelId}>
          {props.label}
          {required && <span className="sr-only">{` ${intl.formatMessage({ id: 'forms.required' })}`}</span>}
        </span>
        {props.info === true && <Info size={14} className="shrink-0 text-text-light" />}
        {props.tooltip !== undefined && <InfoTip label={props.tooltip.label} text={props.tooltip.text} />}
      </span>
      <FieldLabelContext value={labelId}>
        <div
          ref={setFields}
          role={isGroup ? 'group' : undefined}
          aria-labelledby={isGroup ? labelId : undefined}
          className="flex flex-wrap items-center gap-2"
        >
          {props.children}
          {required && (
            <span className="text-text-primary" aria-hidden="true">
              *
            </span>
          )}
        </div>
      </FieldLabelContext>
    </div>
  )
}
